import {Alert, AlertTitle, Button} from '@tinct/web-components';
import {useCurrentOrganization, useIsAgencyWorkspace} from "@tinct/core-workspace";
import {useManageSubscription} from "../../hooks/use-manage-subscription";
import {TinctPlanSummary} from "../../components/tinct-plan-summary";
import {CircleXIcon} from "lucide-react";
import {useSubscriptionCancel} from "../../hooks/use-subscription-cancel";
import {LayoutContent, LayoutHeader, LayoutTopbar} from "@tinct/web-layout";

export function BillingPage() {
  const currentOrganization = useCurrentOrganization();

  const isSubscribed = currentOrganization.subscription !== null;

  // Agency subscriptions are created by the super admin with
  // collection_method=send_invoice, which Stripe's portal flows refuse —
  // agencies cannot self-cancel, so don't offer it.
  const isAgency = useIsAgencyWorkspace();

  const { handleManageSubscription, isPending } = useManageSubscription();
  const { handleSubscriptionCancel, isPending: isCancelPending } = useSubscriptionCancel();

  const cancelAt = currentOrganization.subscription?.cancelAt;
  const subscriptionStatus = currentOrganization.subscription?.status;

  const formatCancelDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const handleReactivate = () => {
    handleManageSubscription();
  };

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar />
      </header>

      <LayoutContent>
        <LayoutHeader
          pageTitle="Billing"
          pageDescription="Manage your subscription and billing information"
        />

        {/* Main Content */}
        <div className="flex flex-col gap-8 max-w-[1056px]">
          {/* Cancellation Alert */}
          {cancelAt && (
            <Alert className="flex items-center justify-between border-rose-200 bg-rose-50">
              <div className="flex items-center gap-3">
                <CircleXIcon className="size-4 text-destructive" />
                <AlertTitle className="text-destructive">
                  Cancels on {formatCancelDate(cancelAt)} : will downgrade to Free
                </AlertTitle>
              </div>
              <Button variant="outline" size="sm" onClick={handleReactivate} disabled={isPending}>
                Reactivate
              </Button>
            </Alert>
          )}

          {subscriptionStatus === 'past_due' && (
            <Alert className="flex items-center justify-between border-rose-200 bg-rose-50">
              <div className="flex items-center gap-3">
                <CircleXIcon className="size-4 text-destructive" />
                <AlertTitle className="text-destructive">
                  Your subscription is past due. Please change your payment method and pay your overdue invoice, or cancel your subscription.
                </AlertTitle>
              </div>
              <Button variant="outline" size="sm" onClick={handleReactivate} disabled={isPending}>
                Update
              </Button>
            </Alert>
          )}

          {/* Manage Subscription Button */}
          <div className="flex items-start">
            <Button variant="outline" disabled={!isSubscribed || isPending}  onClick={handleManageSubscription}>
              {isPending ? 'Redirecting...' : 'Manage subscription'}
            </Button>
          </div>

          {/* Plan Summary */}
          <TinctPlanSummary />

          {/* Cancellation Section */}
          {!cancelAt && !isAgency && (
            <div className="flex items-center justify-between pt-9">
              <h2 className="text-xl font-semibold tracking-tight text-foreground font-heading">
                Cancellation
              </h2>
              <Button variant="destructive" disabled={!isSubscribed || isCancelPending} onClick={handleSubscriptionCancel}>
                Cancel plan
              </Button>
            </div>
          )}
        </div>
      </LayoutContent>
    </div>
  );
}
